import { EarResult } from '../types';

export const DETAILED_RESULTS_STORAGE_KEY = 'jhtDetailedResults';
export const PENDING_SEARCH_KEY = 'jhtPendingSearch';

export const SENSITIVE_RESULTS_QUERY_PARAMS = [
  'name',
  'results',
  'contactid',
  'email',
] as const;

export interface DetailedResultsScores {
  overall: number;
  L: EarResult;
  R: EarResult;
}

export interface DetailedResultsPayload {
  date: number;
  name: string;
  contactid: string;
  hideButtons: boolean;
  results: DetailedResultsScores;
}

const hasScores = (value: unknown): value is DetailedResultsScores => {
  const results = value as DetailedResultsScores | undefined;
  return typeof results?.overall === 'number' && !!results.L && !!results.R;
};

const readStored = (): DetailedResultsPayload | null => {
  const raw = sessionStorage.getItem(DETAILED_RESULTS_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DetailedResultsPayload;
    return hasScores(parsed.results) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveDetailedResults = (payload: DetailedResultsPayload): void => {
  sessionStorage.setItem(DETAILED_RESULTS_STORAGE_KEY, JSON.stringify(payload));
};

/**
 * Shareable cold links (Sheets / Salesforce) still carry scores in the query
 * string. Strip them from what analytics sees while hydrating sessionStorage.
 */
export const buildSafePageLocation = (
  href: string = window.location.href,
): string => {
  const url = new URL(href);
  SENSITIVE_RESULTS_QUERY_PARAMS.forEach((param) => {
    url.searchParams.delete(param);
  });
  return url.toString();
};

export const migrateDetailedResultsFromUrl = (
  search: string = window.location.search,
): boolean => {
  const pendingSearch = sessionStorage.getItem(PENDING_SEARCH_KEY);
  const params = new URLSearchParams(pendingSearch || search);
  const hasSensitiveParam = SENSITIVE_RESULTS_QUERY_PARAMS.some((param) =>
    params.has(param),
  );
  if (!hasSensitiveParam) {
    return false;
  }

  sessionStorage.removeItem(PENDING_SEARCH_KEY);

  // Prefer URL payload for cold opens (Sheet / email links).
  try {
    const results = JSON.parse(params.get('results') || '');
    if (hasScores(results)) {
      saveDetailedResults({
        date: Number(params.get('date')) || Date.now(),
        name: params.get('name') || params.get('email') || '',
        contactid: params.get('contactid') || '',
        hideButtons: params.get('hideButtons') === 'true',
        results,
      });
    }
  } catch {
    // Invalid results JSON — still strip the URL below.
  }

  const url = new URL(window.location.href);
  SENSITIVE_RESULTS_QUERY_PARAMS.forEach((param) => {
    url.searchParams.delete(param);
  });
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  return true;
};

export const loadDetailedResults = (): DetailedResultsPayload | null => {
  // Must run synchronously before first paint of detailed results — bootstrap
  // only stashes the query; migration hydrates storage for the page to read.
  migrateDetailedResultsFromUrl();
  return readStored();
};

/** Build the Salesforce / Sheets shareable URL (includes scores in query). */
export const buildShareableResultsUrl = ({
  origin,
  date,
  name,
  contactid,
  hideButtons,
  results,
}: {
  origin: string;
  date: number;
  name: string;
  contactid: string;
  hideButtons: boolean;
  results: DetailedResultsScores;
}): string => {
  const url = new URL(`${origin}/results/detailedresults`);
  url.searchParams.set('date', String(date));
  url.searchParams.set('name', name);
  url.searchParams.set('contactid', contactid);
  if (hideButtons) {
    url.searchParams.set('hideButtons', 'true');
  }
  url.searchParams.set('results', JSON.stringify(results));
  return url.toString();
};
